import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import LockOutlined from '@mui/icons-material/LockOutlined'
import PersonOutline from '@mui/icons-material/PersonOutline'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import LoginInput from '@component/Auth/LoginInput'
import { SxProps } from '@mui/system'
import classnames from 'classnames'

const useStyles = makeStyles<Theme, { isCard?: boolean }>((theme: Theme) => {
  return {
    card: {
      zIndex: 100,
      backgroundColor: 'white',
      borderRadius: '6px',
      padding: '48px 64px',
      boxShadow: '0 5px 5px 0 rgba(0, 0, 0, 0.10)',
    },
    title: {
      fontWeight: 'bold',
      fontSize: 38,
      color: (props) => (props.isCard ? '#2C3F70' : '#4268B3'),
    },
    subTitle: {
      color: (props) => (props.isCard ? '#707070' : '#949494'),
      marginTop: 8,
      fontSize: 14,
      marginBottom: 48,
    },
    checkLabel: {
      color: theme.palette.grey[600],
      fontSize: 12,
    },
    password: {
      marginTop: 18,
    },
    forgot: {
      fontSize: 12,
      textTransform: 'none',
      color: '#4268B3',
    },
    signBtn: {
      textTransform: 'none',
      backgroundColor: '#4268B3',
      marginTop: (props) => (props.isCard ? 60 : 76),
    },
  }
})

export interface LoginFormInfo {
  email: string
  password: string
  checked: boolean
}

export interface LoginFormProps {
  disabled: boolean
  onSubmit: (value: LoginFormInfo) => void
  onForgot: () => void
  errorText?: string
  onInputChange?: () => void
  sx?: SxProps<Theme>
  isCard?: boolean
}

const errorTextSx = { color: '#D32F2F', fontSize: 13, marginTop: '16px', alignSelf: 'flex-start' }

const LoginForm: React.FC<LoginFormProps> = ({ disabled, onSubmit, onForgot, errorText, onInputChange, sx, isCard }) => {
  const [email, setEmail] = useState('')
  const [key, setKey] = useState('init')
  const [password, setPassword] = useState('')
  const [checked, setChecked] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const onChangeEmail = useCallback(
    (event) => {
      setEmail(event.target.value)
      onInputChange?.()
    },
    [onInputChange]
  )
  const onChangePassword = useCallback(
    (event) => {
      setPassword(event.target.value)
      onInputChange?.()
    },
    [onInputChange]
  )
  const onClickCheck = useCallback(
    (event) => {
      setChecked(event.target.checked)
    },
    [setChecked]
  )
  const onToggleShowPassword = useCallback(
    (event) => {
      setShowPassword(event.target.checked)
    },
    [setShowPassword]
  )
  const onClickSubmit = useCallback(() => {
    onSubmit?.({ email, password, checked })
  }, [email, password, checked])
  const onClickForgot = useCallback(() => {
    onForgot?.()
  }, [onForgot])

  useEffect(() => {
    const handle = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        onClickSubmit()
      }
    }
    document.getElementById('password')?.addEventListener('keypress', handle)
    return () => {
      document.getElementById('password')?.removeEventListener('keypress', handle)
    }
  }, [onClickSubmit])

  useEffect(() => {
    // Passwords are never stored: a copy saved before a reset silently goes
    // stale and the masked prefill re-submits it (specs#3346). Purge the key
    // legacy installs still carry.
    localStorage.removeItem('password')
    const email = localStorage.getItem('user')
    if (email) {
      setEmail(email)
      setChecked(true)
    }
  }, [])

  const classes = useStyles({ isCard })

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ...(sx || {}) }}
      className={classnames({ [classes.card]: isCard })}
    >
      <Typography component="h1" variant="h5" className={classes.title}>
        Welcome!
      </Typography>
      <Typography component="h6" variant="subtitle2" className={classes.subTitle}>
        Please enter your details.
      </Typography>
      <LoginInput
        id="email"
        placeholder="Email Address"
        name="email"
        onChange={onChangeEmail}
        icon={<PersonOutline htmlColor="#fff" />}
        value={email}
        autoComplete="off"
      />
      <LoginInput
        key={`${key}-password`}
        name="password"
        placeholder="Password"
        id="password"
        type={showPassword ? 'text' : 'password'}
        className={classes.password}
        value={password}
        onChange={onChangePassword}
        autoComplete="new-password"
        icon={<LockOutlined htmlColor="#fff" />}
      />
      <Grid container alignItems="center" justifyContent="space-between" sx={{ marginTop: '12px' }}>
        <Grid item>
          <FormControlLabel
            classes={{ root: classes.checkLabel }}
            control={<Checkbox value="remember" color="primary" checked={checked} onChange={onClickCheck} />}
            label="Remember me"
          />
          <FormControlLabel
            classes={{ root: classes.checkLabel }}
            control={<Checkbox value="show-password" color="primary" checked={showPassword} onChange={onToggleShowPassword} />}
            label="Show password"
          />
        </Grid>
        <Grid item>
          <Button variant="text" classes={{ root: classes.forgot }} onClick={onClickForgot}>
            Forgot Password?
          </Button>
        </Grid>
      </Grid>
      {!!errorText && (
        <Typography role="alert" sx={errorTextSx}>
          {errorText}
        </Typography>
      )}
      <Button
        disabled={disabled}
        onClick={onClickSubmit}
        fullWidth
        color="primary"
        classes={{
          root: classes.signBtn,
        }}
        variant="contained"
      >
        Sign In
      </Button>
    </Box>
  )
}

export default LoginForm
